// iter183 — Guest breakdown formatter.
// Renders "4 Adults, 2 Children" when both counts are present, "4 Adults" when
// only adults are set, and falls back to the aggregate `no_of_guests` when the
// per-category counts aren't available on the booking.
//
// iter184 — For places that must render the value **exactly as stored**
// (no parsing, no reformatting, no defaulting to 0), use ``rawGuestValue``.
// The calendar day-detail popup uses the raw helper so whatever the OTA /
// admin panel wrote in the `guests` field is shown verbatim.

const toInt = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * Render the Guests value **exactly as stored** on the booking record.
 * No parsing, no reformatting, no "0 Guests" fallback. Only returns
 * "—" when the field is truly absent / null / empty string.
 *
 * Field priority: ``no_of_guests_raw`` > ``no_of_guests`` > ``guests``
 * (the first one that exists on the record wins — whichever the source
 * layer chose to publish is shown verbatim).
 */
export const rawGuestValue = (booking) => {
  if (!booking) return '—';
  for (const key of ['no_of_guests_raw', 'no_of_guests', 'guests']) {
    if (Object.prototype.hasOwnProperty.call(booking, key)) {
      const v = booking[key];
      if (v === null || v === undefined) continue;
      if (typeof v === 'string') {
        const t = v.trim();
        if (t) return t;
        continue;
      }
      // numbers, booleans, etc — render as-is (String() preserves 0).
      return String(v);
    }
  }
  return '—';
};

/**
 * Format guest counts on a booking as adults + children.
 * @param {object} booking Booking-like record. Understood fields:
 *   - `no_of_adults`, `no_of_children` (preferred split)
 *   - `no_of_guests` (aggregate)
 *   - `no_of_guests_raw` / `guests` (raw source string — used verbatim
 *      when it isn't purely numeric)
 * @returns {string} Human-readable breakdown, or "—" when nothing is
 *   available.
 */
export const formatGuestBreakdown = (booking) => {
  if (!booking) return '—';

  // iter183 — Prefer a raw source string when it carries formatting
  // (letters/commas/etc). Purely numeric strings fall through to the
  // aggregate handling below.
  const raw = booking.no_of_guests_raw ?? booking.guests;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed && /[^0-9]/.test(trimmed) && !/^\d+$/.test(trimmed)) {
      return trimmed;
    }
  }

  const adults = toInt(booking.no_of_adults);
  const children = toInt(booking.no_of_children);

  if (adults > 0 || children > 0) {
    const parts = [];
    if (adults > 0) parts.push(`${adults} ${adults === 1 ? 'Adult' : 'Adults'}`);
    if (children > 0) parts.push(`${children} ${children === 1 ? 'Child' : 'Children'}`);
    return parts.join(', ');
  }

  const total = toInt(booking.no_of_guests) || toInt(raw);
  if (total > 0) return `${total} ${total === 1 ? 'Guest' : 'Guests'}`;

  return '—';
};
